import React from "react";
import { useTranslation } from "react-i18next";
import { DELIVERY_MODE } from "constants/moduleConstants/projectInfo.constant";
import {
  getProjectInitials,
  getProjectLocationText,
} from "utils/projectSelectionHelpers";
import ProjectLocationContent from "./ProjectLocationContent";
import "views/landing/ProjectSelection/project-selection-card.css";

const PinIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

const GiftIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7" />
    <path d="M12 7H7.5a2.5 2.5 0 1 1 2.5-2.5C10 7 12 7 12 7Zm0 0h4.5A2.5 2.5 0 1 0 14 4.5C14 7 12 7 12 7Z" />
  </svg>
);

const HeartIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
  </svg>
);

const getScopeLabel = (project, activeMode, t) => {
  const mode = project.projectinfo?.deliveryMode || activeMode;
  if (mode === DELIVERY_MODE.BIN) {
    return t("projectSelection.scope.bin");
  }
  if (mode === DELIVERY_MODE.HOME) {
    return t("projectSelection.scope.home");
  }
  return t("projectSelection.scope.general");
};

const ProjectSelectionCard = ({
  project,
  selected = false,
  onSelect,
  preview = false,
  activeMode = DELIVERY_MODE.HOME,
}) => {
  const { t } = useTranslation();
  const info = project.projectinfo;
  const locationText = getProjectLocationText(project, activeMode);
  const giftText = info?.giftText || "";
  const donationText = info?.donationText || "";
  const initials = getProjectInitials(project.name);
  const isSelected = preview || selected;

  const handleClick = () => {
    if (!preview && onSelect) {
      onSelect(project);
    }
  };

  const handleKeyDown = (event) => {
    if (preview) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect?.(project);
    }
  };

  return (
    <article
      className={`project-selection-card${isSelected ? " selected" : ""}${
        preview ? " preview" : ""
      }`}
      style={{ "--project-brand": "#11b67a" }}
      role={preview ? undefined : "radio"}
      aria-checked={preview ? undefined : isSelected}
      tabIndex={preview ? undefined : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="project-selection-card__brand" aria-hidden="true">
        {project.logo ? (
          <img src={project.logo} alt={project.name} />
        ) : (
          initials
        )}
      </div>

      <div>
        <h3 className="project-selection-card__name">{project.name}</h3>
        <div className="project-selection-card__tags">
          <span className="project-selection-card__tag green">
            {t("projectSelection.activeProjectTag")}
          </span>
          <span className="project-selection-card__tag">
            {getScopeLabel(project, activeMode, t)}
          </span>
        </div>
      </div>

      <div className="project-selection-card__detail">
        {locationText && (
          <div className="project-selection-card__address">
            <PinIcon />
            <ProjectLocationContent content={locationText} />
          </div>
        )}

        {(giftText || donationText) && (
          <div className="project-selection-card__rewards">
            {giftText && (
              <div className="project-selection-card__reward">
                <span className="project-selection-card__reward-icon">
                  <GiftIcon />
                </span>
                <span className="project-selection-card__reward-copy">
                  <span>{t("projectSelection.giftLabel")}</span>
                  <strong>{giftText}</strong>
                </span>
              </div>
            )}
            {donationText && (
              <div className="project-selection-card__reward">
                <span className="project-selection-card__reward-icon">
                  <HeartIcon />
                </span>
                <span className="project-selection-card__reward-copy">
                  <span>{t("projectSelection.donationLabel")}</span>
                  <strong>{donationText}</strong>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

export default ProjectSelectionCard;
